import { ReactElement, ReactNode } from 'react';
import amerenAnnualUse from 'static/ameren-il-yearly-kwh-desktop.jpg';
import eversourceMeter from 'static/eversource-meter-usage-english-desktop.png';
import eversourceRateClass from 'static/rate-class/ma/eversource_ma.png';
import natGridRateClass from 'static/rate-class/ma/nat_grid_ma.png';
import unitilRateClass from 'static/rate-class/ma/unitil_rate_class.png';
import unitilAnnualUse from 'static/unitil-ma-annual-kwh-desktop.jpg';
import unitilNameKeyForEdi from 'static/unitil-name-key-for-edi.png';

// Image imports
import amerenDailyUse from 'static/utility-bills/il/ameren-daily-use.png';
import amerenMeter from 'static/utility-bills/il/ameren-meter.png';
import amerenUtilityNumber from 'static/utility-bills/il/ameren-utility-number.png';
import comedDailyUse from 'static/utility-bills/il/comed-daily-use.png';
import comedElectricChoice from 'static/utility-bills/il/comed-electric-choice.png';
import comedMeter from 'static/utility-bills/il/comed-meter.png';
import comedUtilityNumber from 'static/utility-bills/il/comed-utility-number.png';
import eversourceMaMonthlyUsage from 'static/utility-bills/ma/eversource-monthly-usage.png';
import natGridMaMonthlyUsage from 'static/utility-bills/ma/nat-grid-monthly-usage.png';
import unitilUsage from 'static/utility-bills/ma/unitil-ma-usage.png';
import xcelBill from 'static/utility-bills/mn/xcel-bill.png';
import xcelKwh from 'static/utility-bills/mn/xcel-kwh.png';
import xcelPremise from 'static/utility-bills/mn/xcel-premise.png';
import conEdison from 'static/utility-bills/ny/Conedison.jpg';
import centralHudson from 'static/utility-bills/ny/centralhudson.png';
import nationalGrid from 'static/utility-bills/ny/nationalgrid.png';
import nyseg from 'static/utility-bills/ny/nyseg.png';
import rge from 'static/utility-bills/ny/rge.png';
import rockland from 'static/utility-bills/ny/rockland.png';

import { useDialog } from '~/hooks';
import { IS_PERCH_ENERGY, ORGANIZATION_ENTITIES, UTILITY_IDS } from '~/utils/constants';
import { store } from '~/utils/store';

import { DialogSection } from '../dialog-section.component';
import { StyledModal as Dialog } from '../dialog.style';
import { HelpIconWrapper, SubtitleList } from './enrollment-fields-explainer.style';

interface HelperProps {
  target: ReactElement;
  copy?: {
    fieldName: string;
    fieldLabel?: string;
  };
}
const UnitilAnnualUsageHelper = () => (
  <ul>
    <li>
      Please refer to the ‘Electric Usage (kWh)’ graph on your Unitil bill and add up your estimated
      usage for each month.
    </li>
    <br />

    <li>
      <em>Est. annual usage = Jan usage + Feb usage... + Dec usage</em>
    </li>
    <br />
    <li>
      If fewer than 12 months of usage history are available, add up the provided usage, divide by
      the number of months available, and multiply by 12.
    </li>
    <br />
    <li>
      <em>Est. annual usage = Average monthly usage X 12</em>
    </li>
  </ul>
);

const getHelperLabels = (utilityId, fieldLabel) => {
  const entityName = IS_PERCH_ENERGY ? ORGANIZATION_ENTITIES.PERCH : ORGANIZATION_ENTITIES.ARCADIA;
  const utilityAccountDescription = `${entityName} connects to your utility account to apply savings directly to your electricity bill. You’ll still pay your utility company - you'll just pay a reduced amount.`;
  const labels = {
    utilityName: '',
    loginLink: '/',
    estimatedDailyKwh: {
      title: '',
      subtitle: '',
      image: '',
    },
    estimatedAnnualKwh: {
      title: '',
      subtitle: '',
      image: '',
    },
    login: {
      title: '',
      subtitle: '',
      image: '',
    },
    meterNumber: {
      title: '',
      subtitle: '',
      image: '',
    },
    secondaryIdentifier: {
      title: '',
      subtitle: '',
      image: '',
    },
    premises: {
      title: '',
      subtitle: '',
      image: '',
    },
    monthlyUsage: {
      title: '',
      subtitle: '',
      image: '',
    },
    rateClass: {
      title: '',
      subtitle: '' as ReactNode,
      image: '',
    },
    utilityAccountNumber: {
      title: 'Why do we need your account number?',
      subtitle: utilityAccountDescription,
      image: '',
      loginLink: '',
    },
    nameKeyForEdi: {
      title: 'Name Key for EDI',
      subtitle: 'Find your Name key on your utility bill',
      image: '',
    },
    'Electric Choice ID': {
      title: 'Electric Choice Id',
      subtitle:
        'Electric Choice Id can be found on the second page of your electricity bill, on the right-hand side.',
      image: comedElectricChoice,
    },
  };
  if (fieldLabel === 'Electric Choice ID') {
    labels.secondaryIdentifier = {
      title: 'Electric Choice Id',
      subtitle:
        'Electric Choice Id can be found on the second page of your electricity bill, on the right-hand side.',
      image: comedElectricChoice,
    };
  }
  switch (parseInt(utilityId)) {
    case UTILITY_IDS['Xcel Energy MN']: {
      labels.utilityName = 'Xcel Energy';
      labels.loginLink = 'https://my.xcelenergy.com/MyAccount/XE_Login?template=XE_MA_Template';
      labels.estimatedDailyKwh = {
        title: 'Average daily electric use',
        subtitle:
          'Average daily electric use is required to calculate your annual usage. You can find it on the first page of your electricity bill, on the right-hand side.',
        image: xcelKwh,
      };
      labels.premises = {
        title: 'Premises Number',
        subtitle: 'Can be found on the second page under your Electricity Service Details.',
        image: xcelPremise,
      };
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: xcelBill,
        loginLink: 'https://my.xcelenergy.com/MyAccount/XE_Login?template=XE_MA_Template',
      };
      return labels;
    }
    case UTILITY_IDS['Ameren IL']: {
      labels.utilityName = 'Ameren';
      labels.loginLink = 'https://www.ameren.com/login-page';
      labels.login = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: amerenUtilityNumber,
      };
      labels.estimatedDailyKwh = {
        title: 'Average daily electric use',
        subtitle:
          'Average daily electric use is required to calculate your annual usage. You can find it on the first page of your electricity bill, on the right-hand side.',
        image: amerenDailyUse,
      };
      labels.meterNumber = {
        title: 'Meter number',
        subtitle:
          'Meter number can be found on the third page of your electricity bill, on the left-hand side.',
        image: amerenMeter,
      };
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: `${entityName} connects to your utility account to apply savings directly to your electricity bill. You’ll still pay your utility company - you'll just pay a lower rate.`,
        image: amerenUtilityNumber,
        loginLink: 'https://www.ameren.com/login-page',
      };
      labels.estimatedAnnualKwh = {
        title: 'Average annual use',
        subtitle:
          'Please add up your monthly kWh usage. If fewer than 12 months are available, divide by the number of months available and multiply by 12.',
        image: amerenAnnualUse,
      };
      return labels;
    }
    case UTILITY_IDS['Commonwealth Edison']: {
      labels.utilityName = 'Comed';
      labels.loginLink = 'https://secure.comed.com/accounts/login';
      labels.meterNumber = {
        title: 'Meter number',
        subtitle:
          'Meter number can be found on the second page of your electricity bill, on the left-hand side.',
        image: comedMeter,
      };
      labels.login = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: comedUtilityNumber,
      };
      labels.estimatedDailyKwh = {
        title: 'Average daily use',
        subtitle:
          'Average daily use is required to calculate your annual usage. You can find it on the first page of your electricity bill, on the right-hand side.',
        image: comedDailyUse,
      };
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: comedUtilityNumber,
        loginLink: 'https://secure.comed.com/accounts/login',
      };
      return labels;
    }
    case UTILITY_IDS['Central Hudson Gas & Electric']: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: centralHudson,
        loginLink: 'https://www.cenhud.com/en/login/',
      };
      return labels;
    }
    case UTILITY_IDS['Consolidated Edison NY']: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: conEdison,
        loginLink: 'https://www.coned.com/en/login',
      };
      return labels;
    }
    case UTILITY_IDS.NYSEG: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: nyseg,
        loginLink:
          'https://sso.nyseg.com/login?p_p_state=normal&p_p_mode=view&_com_liferay_login_web_portlet_LoginPortlet_mvcRenderCommandName=%2Flogin%2Flogin&saveLastPath=false&p_p_id=com_liferay_login_web_portlet_LoginPortlet&p_p_lifecycle=0&_com_liferay_login_web_portlet_LoginPortlet_redirect=%2Fc%2Fportal%2Fsaml%2Fsso%3Fsaml_message_id%3D_29eda00592626a3b82ff957e1316f1211730174b',
      };
      return labels;
    }
    case UTILITY_IDS['National Grid NY']: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: nationalGrid,
        loginLink: 'https://www.nationalgridus.com/Default.aspx',
      };
      return labels;
    }
    case UTILITY_IDS['Rochester Gas & Electric']: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: rge,
        loginLink: 'https://sso.rge.com/',
      };
      return labels;
    }
    case UTILITY_IDS['Orange and Rockland']: {
      labels.utilityAccountNumber = {
        title: 'Why do we need your account number?',
        subtitle: utilityAccountDescription,
        image: rockland,
        loginLink: 'https://www.oru.com/en/accounts-billing/my-account',
      };
      return labels;
    }
    case UTILITY_IDS['National Grid MA']: {
      labels.monthlyUsage = {
        title: 'Monthly kWh',
        subtitle:
          'Your bill shows a breakdown of your kWh usage located on the left of your utility bill. Please ensure that we’ve captured your kWh accurately.',
        image: natGridMaMonthlyUsage,
      };
      labels.rateClass = {
        title: 'Rate Class',
        subtitle:
          'Rate Class can be found on the first page of your electricity bill, on the right-hand side.',
        image: natGridRateClass,
      };
      return labels;
    }
    case UTILITY_IDS['Eversource / WMECO']:
    case UTILITY_IDS['Eversource / NSTAR']: {
      labels.meterNumber = {
        title: 'Meter number',
        subtitle:
          'The 7 digit meter number can be found on the second page of your Eversource NSTAR utility bill',
        image: eversourceMeter,
      };
      labels.monthlyUsage = {
        title: 'Monthly kWh',
        subtitle: '',
        image: eversourceMaMonthlyUsage,
      };
      labels.rateClass = {
        title: 'Rate Class',
        subtitle: (
          <SubtitleList>
            <li>
              On page 2 of your electricity bill, look for ‘Rate’ under Service Address or Delivery
              Charges.
            </li>
            <li>
              Check if the Rate contains R1, R2, or R4, then select the matching rate on screen.
            </li>
            <li>
              <strong>
                Example: If your rate class is Rate R1 – Residential Non-Heating on the bill, select
                ‘R1’.
              </strong>
            </li>
          </SubtitleList>
        ),
        image: eversourceRateClass,
      };
      return labels;
    }
    case UTILITY_IDS['Unitil MA']: {
      labels.estimatedAnnualKwh = {
        title: 'Estimated annual kWh',
        subtitle:
          'Please refer to the "Electric Usage (kWh)" graph on your Unitil bill and add up your estimated usage for each month. If fewer than 12 months of usage history are available, add up the provided usage, divide by the number of months available, and multiply by 12.',
        image: unitilAnnualUse,
      };
      labels.nameKeyForEdi = {
        title: 'Name Key for EDI',
        subtitle: 'Find your Name key on your utility bill',
        image: unitilNameKeyForEdi,
      };
      labels.monthlyUsage = {
        title: 'Metered usage',
        subtitle:
          'Select the month from your bill and enter the metered usage shown on your Unitil electric bill. We use this to estimate your annual usage.',
        image: unitilUsage,
      };
      labels.rateClass = {
        title: 'Rate Class',
        subtitle:
          'Rate Class can be found on the first page of your electricity bill, on the right-hand side.',
        image: unitilRateClass,
      };
      return labels;
    }
    default:
      return labels;
  }
};

export const EnrollmentFieldsExplainerDialog = ({ target, copy }: HelperProps) => {
  const { isOpen, close, TargetComponent } = useDialog(target);
  const fieldName = copy?.fieldName || '';
  const utilityId = store.get('UTILITY_SELECTION')?.id || '';
  const labels = getHelperLabels(utilityId, copy?.fieldLabel);
  const shouldShowDialog =
    utilityId &&
    fieldName &&
    typeof fieldName === 'string' &&
    labels[fieldName]?.title &&
    labels[fieldName]?.image;

  const renderSubtitle = () => {
    if (fieldName === 'estimatedAnnualKwh' && utilityId === UTILITY_IDS['Unitil MA']) {
      return <UnitilAnnualUsageHelper />;
    }
    const subtitle = labels[fieldName]?.subtitle;
    // Element subtitles render their own block markup; wrapping a list in <p> is invalid HTML.
    if (typeof subtitle === 'string') return <p>{subtitle}</p>;
    return subtitle;
  };

  return (
    <>
      <HelpIconWrapper className={!shouldShowDialog ? 'hide' : ''}>
        {TargetComponent}
      </HelpIconWrapper>
      {!!shouldShowDialog && (
        <Dialog isOpen={isOpen} onDismiss={close} aria-label="Enrollment Fields Explainer">
          <DialogSection title={labels[fieldName]?.title}>
            {renderSubtitle()}
            <div style={{ margin: '16px 0', textAlign: 'center' }}>
              <img src={labels[fieldName]?.image} />
            </div>
          </DialogSection>
          <Dialog.FooterStacked primaryText="Got it" onSubmit={close} />
        </Dialog>
      )}
    </>
  );
};
